import { Transfer, TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

/**
 * Decides whether a session belongs to the DA Flexible Note flow or the legacy close-session flow
 * (ICE-7346).
 *
 *     note exists     → content_meta_data.name === daFlexibleNote ? new flow : old flow
 *     note not exists → charge exists ? old flow : new flow
 *
 * Charge *status* is irrelevant — only existence, and only when there is no note. That works because the
 * new flow refuses to create a charge without a saved flexible note
 * (`useFlexibleNoteCloseSession`'s `missingNoteId` abort) while the legacy flow has no such requirement.
 * So a charge with no note can only have come from the legacy flow, which is exactly the ICE-7346 state:
 * a legacy invoice was submitted, the note was never written, and serving the merged note would make the
 * provider re-submit the payment — rejected by the API as `duplicate_payment`.
 *
 * The previous check queried `status: pending_note` only. The API's duplicate rule rejects
 * `submitted`/`pending`/`approved`/`t0_sessions`, which is disjoint from that, so the legacy charge was
 * invisible and the merged note was served for an already-paid session.
 */

interface TransfersQueryResult {
  data?: Transfer[]
}

type FetchTransfers = (args: {
  provider_id: string
  patient_id: string
  appointment_id: number
  showAlert: boolean
}) => { unwrap: () => Promise<TransfersQueryResult> }

export interface AppointmentChargeGuardResult {
  /** A charge of any status exists for this appointment. The only charge input to the flow decision. */
  hasCharge: boolean
  /**
   * The `pending_note` charge, if any — used only as the `chargeId` passed on to the note screen, never
   * to pick a flow. It must stay `pending_note`-only because that is the sole status the note-completion
   * step may flip to `accept`; handing it a finalized charge would accept that charge a second time.
   */
  pendingNoteCharge?: Transfer
  /** The lookup failed, so the caller cannot decide a flow and should surface an error instead. */
  failed: boolean
}

/**
 * Fetches the charges for a single appointment. Scoped by `appointment_id` with no status filter, so the
 * response is a couple of rows at most and paging never comes into play.
 */
export const fetchAppointmentChargeGuard = async (
  fetchTransfers: FetchTransfers,
  { providerId, clientId, appointmentId }: { providerId: string; clientId: string; appointmentId: number },
): Promise<AppointmentChargeGuardResult> => {
  try {
    const result = await fetchTransfers({
      provider_id: providerId,
      patient_id: clientId,
      appointment_id: appointmentId,
      showAlert: false,
    }).unwrap()

    // The API filters by `appointment_id`, but re-filter here so the result can never include a charge
    // belonging to another appointment (or an unattributed charge with a null `appointment_id`).
    const charges = (result?.data ?? []).filter((charge) => charge?.appointment_id === appointmentId)

    return {
      hasCharge: charges.length > 0,
      pendingNoteCharge: charges.find((charge) => charge?.status === TransferStatus.PENDING_NOTE),
      failed: false,
    }
  } catch {
    return { hasCharge: false, pendingNoteCharge: undefined, failed: true }
  }
}
